import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { urlConfig, getImageUrl } from '../../config';
import useGifts from '../../hooks/useGifts';
import GiftImage, { getConditionBadgeClass } from '../common/GiftImage';
import { GiftGridSkeleton } from '../common/GiftCardSkeleton';
import '../common/GiftImage.css';
import '../common/GiftCardSkeleton.css';
import './SearchPage.css';

function SearchPage() {
    const { gifts: allGifts, setGifts, ready, syncing } = useGifts();
    const [searchQuery, setSearchQuery] = useState('');
    const [category, setCategory] = useState('');
    const [condition, setCondition] = useState('');
    const [ageYears, setAgeYears] = useState(10);
    const [searchResults, setSearchResults] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [searching, setSearching] = useState(false);

    const categories = ['Living Room', 'Bedroom', 'Bathroom', 'Kitchen', 'Office'];
    const conditions = ['New', 'Like New', 'Older'];
    const navigate = useNavigate();

    const displayedGifts = searchResults ?? allGifts;
    const showSkeleton = !ready && allGifts.length === 0;

    const handleSearch = async () => {
        setSearching(true);
        try {
            const params = new URLSearchParams();
            if (searchQuery) params.append('name', searchQuery);
            if (category) params.append('category', category);
            if (condition) params.append('condition', condition);
            params.append('age_years', ageYears);

            const response = await fetch(`${urlConfig.backendUrl}/api/search?${params.toString()}`);
            if (!response.ok) throw new Error('Search failed');
            const data = await response.json();
            setSearchResults(data);
            setHasSearched(true);
        } catch (error) {
            console.log('Search error: ' + error.message);
            const filtered = filterGiftsLocally(allGifts);
            setSearchResults(filtered);
            setHasSearched(true);
        } finally {
            setSearching(false);
        }
    };

    const filterGiftsLocally = (gifts) => {
        return gifts.filter((gift) => {
            if (searchQuery && !gift.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
            if (category && gift.category !== category) return false;
            if (condition && gift.condition !== condition) return false;
            if (gift.age_years > ageYears) return false;
            return true;
        });
    };

    const clearFilters = () => {
        setSearchQuery('');
        setCategory('');
        setCondition('');
        setAgeYears(10);
        setHasSearched(false);
        setSearchResults(null);
        fetch(`${urlConfig.backendUrl}/api/gifts`)
            .then((r) => (r.ok ? r.json() : allGifts))
            .then((data) => {
                if (Array.isArray(data) && data.length) {
                    setGifts(data);
                }
            })
            .catch(() => {});
    };

    return (
        <div className="page-container">
            <header className="page-header">
                <h1>Find your next find</h1>
                <p>Search by name, category, condition, or age — and discover items waiting for a new home.</p>
            </header>

            <div className="search-layout">
                <aside className="search-filters">
                    <h3>Filters</h3>

                    <div className="filter-group">
                        <label htmlFor="search-name">Search by name</label>
                        <input
                            id="search-name"
                            type="text"
                            className="dropdown-filter"
                            placeholder="e.g. Lamp, Table..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="search-category">Category</label>
                        <select
                            id="search-category"
                            className="dropdown-filter"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="search-condition">Condition</label>
                        <select
                            id="search-condition"
                            className="dropdown-filter"
                            value={condition}
                            onChange={(e) => setCondition(e.target.value)}
                        >
                            <option value="">All Conditions</option>
                            {conditions.map((cond) => (
                                <option key={cond} value={cond}>{cond}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="search-age">
                            Max age: <span className="age-value">{ageYears} years</span>
                        </label>
                        <input
                            id="search-age"
                            type="range"
                            min="0"
                            max="10"
                            value={ageYears}
                            onChange={(e) => setAgeYears(parseInt(e.target.value))}
                        />
                    </div>

                    <button
                        className="btn-gift btn-gift-primary"
                        style={{ width: '100%', marginBottom: '0.75rem' }}
                        onClick={handleSearch}
                        disabled={searching}
                    >
                        {searching ? 'Searching…' : 'Search'}
                    </button>
                    <button className="btn-gift btn-gift-secondary" style={{ width: '100%' }} onClick={clearFilters}>
                        Clear Filters
                    </button>
                </aside>

                <section className="search-results">
                    <div className="search-results-header">
                        <h2>{hasSearched ? 'Search Results' : 'All Gifts'}</h2>
                        <span className="results-count">
                            {displayedGifts.length} item{displayedGifts.length !== 1 ? 's' : ''} found
                            {syncing && !hasSearched && ' · Updating…'}
                        </span>
                    </div>

                    {showSkeleton ? (
                        <GiftGridSkeleton count={6} />
                    ) : displayedGifts.length === 0 ? (
                        <div className="no-results-box">
                            <p style={{ margin: 0, fontWeight: 600 }}>No gifts match your criteria</p>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="gifts-grid">
                            {displayedGifts.map((gift) => (
                                <article
                                    key={gift.id}
                                    className="search-result-card"
                                    onClick={() => navigate(`/app/gift/${gift.id}`)}
                                >
                                    <div className="search-result-card__media">
                                        <GiftImage
                                            name={gift.name}
                                            category={gift.category}
                                            imageUrl={getImageUrl(gift.image)}
                                            variant="search"
                                        />
                                    </div>
                                    <div className="search-result-card__body">
                                        <h4>{gift.name}</h4>
                                        <div className="gift-card__meta">
                                            <span className={`badge-condition ${getConditionBadgeClass(gift.condition)}`}>
                                                {gift.condition}
                                            </span>
                                            <span className="badge-category">{gift.category}</span>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

export default SearchPage;
